import { ChevronDown, Search } from 'lucide-react'
import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react'
import { PF_MESSAGE_COPY as COPY, selectablePeople, type PfContact } from '../model/pfMessages'

/**
 * C-14's "Search people" (2026-10-16): an editable combobox with a list popup. Focusing or clicking the box (or the
 * chevron) opens every person who can still be picked, A–Z; typing filters the open list. Picked people leave the
 * list (they are the chips above it) and the list stays open for the next pick. Keyboard: ArrowDown opens and moves,
 * ArrowUp moves, Enter picks the highlighted person, Escape closes, Tab leaves; a click outside closes. Focus stays
 * in the box (`aria-activedescendant` follows the highlight).
 */
export function RecipientPicker({
  contacts,
  onPick,
}: {
  contacts: readonly PfContact[]
  onPick: (id: string) => void
}) {
  const [q, setQ] = useState('')
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(-1)
  const wrapRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const baseId = useId()
  const listId = `${baseId}-list`
  const optionId = (i: number) => `${baseId}-opt-${String(i)}`

  const options = selectablePeople(contacts, q)
  const activeIndex = active < options.length ? active : options.length - 1

  const show = () => {
    if (!open) {
      setOpen(true)
      setActive(-1)
    }
  }
  const hide = () => {
    setOpen(false)
    setActive(-1)
  }
  const pick = (i: number) => {
    const person = options[i]
    if (!person) return
    onPick(person.id)
    // The next person moves up into this place (keyboard picks in a row); a filtered list starts over.
    setActive(q === '' ? Math.min(i, options.length - 2) : -1)
    setQ('')
  }

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false)
        setActive(-1)
      }
    }
    document.addEventListener('mousedown', onDoc)
    return () => {
      document.removeEventListener('mousedown', onDoc)
    }
  }, [open])

  useEffect(() => {
    if (open && activeIndex >= 0) document.getElementById(optionId(activeIndex))?.scrollIntoView({ block: 'nearest' })
    // optionId is derived from the stable baseId.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeIndex])

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        if (!open) {
          setOpen(true)
          setActive(options.length > 0 ? 0 : -1)
        } else setActive(Math.min(activeIndex + 1, options.length - 1))
        return
      case 'ArrowUp':
        e.preventDefault()
        if (!open) {
          setOpen(true)
          setActive(options.length - 1)
        } else setActive(Math.max(activeIndex - 1, 0))
        return
      case 'Enter':
        if (open && activeIndex >= 0) {
          e.preventDefault()
          pick(activeIndex)
        }
        return
      case 'Escape':
        if (open) {
          e.preventDefault()
          // Close only this list, not anything around it.
          e.stopPropagation()
          hide()
        }
        return
      case 'Tab':
        hide()
        return
    }
  }

  const activeId = open && activeIndex >= 0 ? optionId(activeIndex) : undefined
  return (
    <div className="pfm-search" ref={wrapRef}>
      <Search aria-hidden />
      <input
        ref={inputRef}
        className="input"
        type="text"
        role="combobox"
        autoComplete="off"
        placeholder={COPY.search}
        aria-label={COPY.search}
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={activeId}
        value={q}
        onFocus={show}
        onClick={show}
        onChange={(e) => {
          setQ(e.target.value)
          setOpen(true)
          // A typed name: Enter picks the first match.
          setActive(selectablePeople(contacts, e.target.value).length > 0 ? 0 : -1)
        }}
        onKeyDown={onKeyDown}
      />
      <button
        type="button"
        className="btn btn-ghost pfm-toggle"
        // The box is the tab stop; the chevron is for the mouse (the keyboard opens the list with ArrowDown).
        tabIndex={-1}
        aria-label={open ? COPY.hidePeople : COPY.showPeople}
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        onMouseDown={(e) => {
          // Keep the focus (and the caret) in the box.
          e.preventDefault()
        }}
        onClick={() => {
          if (open) hide()
          else {
            show()
            inputRef.current?.focus()
          }
        }}
      >
        <ChevronDown aria-hidden />
      </button>
      {open && (
        <div className="select-menu pfm-menu">
          {options.length === 0 ? (
            <div className="select-empty" role="status" id={listId}>
              {q.trim() === '' ? COPY.everyonePicked : COPY.noMatches}
            </div>
          ) : (
            <div role="listbox" id={listId} aria-label={COPY.people}>
              {options.map((c, i) => (
                // Keyboard is handled on the combobox (aria-activedescendant); options are not tab stops.
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                <div
                  key={c.id}
                  id={optionId(i)}
                  role="option"
                  tabIndex={-1}
                  aria-selected={i === activeIndex}
                  className={i === activeIndex ? 'select-option active' : 'select-option'}
                  onMouseDown={(e) => {
                    e.preventDefault()
                  }}
                  onMouseEnter={() => {
                    setActive(i)
                  }}
                  onClick={() => {
                    pick(i)
                    inputRef.current?.focus()
                  }}
                >
                  <span className="pfm-option-name">{c.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
